/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import * as GranularityAPI from 'GranularityAPI';

export const ADD_GRAIN = 'ADD_GRAIN';
export const REMOVE_GRAIN = 'REMOVE_GRAIN';
export const UPDATE_GRAIN = 'UPDATE_GRAIN';

export const GET_AVAILABLE_AGGREGATES = 'GET_AVAILABLE_AGGREGATES';
export const GET_AVAILABLE_AGGREGATES_FAILURE = 'GET_AVAILABLE_AGGREGATES_FAILURE';
export const GET_AVAILABLE_AGGREGATES_SUCCESS = 'GET_AVAILABLE_AGGREGATES_SUCCESS';

export const GET_GRANULARITIES = 'GET_GRANULARITIES';
export const GET_GRANULARITIES_FAILURE = 'GET_GRANULARITIES_FAILURE';
export const GET_GRANULARITIES_SUCCESS = 'GET_GRANULARITIES_SUCCESS';

export const ADD_GRANULARITY = 'ADD_GRANULARITY';
export const ADD_GRANULARITY_FAILURE = 'ADD_GRANULARITY_FAILURE';
export const ADD_GRANULARITY_SUCCESS = 'ADD_GRANULARITY_SUCCESS';

export const REMOVE_GRANULARITY = 'REMOVE_GRANULARITY';
export const REMOVE_GRANULARITY_FAILURE = 'REMOVE_GRANULARITY_FAILURE';
export const REMOVE_GRANULARITY_SUCCESS = 'REMOVE_GRANULARITY_SUCCESS';

export const UPDATE_GRANULARITY = 'UPDATE_GRANULARITY';
export const UPDATE_GRANULARITY_FAILURE = 'UPDATE_GRANULARITY_FAILURE';
export const UPDATE_GRANULARITY_SUCCESS = 'UPDATE_GRANULARITY_SUCCESS';

// ---------------
// Available aggregates Action
// ---------------
const getAvailableAggregates = () => ({
  type: GET_AVAILABLE_AGGREGATES,
});

const getAvailableAggregatesFailure = error => ({
  type: GET_AVAILABLE_AGGREGATES_FAILURE,
  payload: error,
});

const getAvailableAggregatesSuccess = aggregates => ({
  type: GET_AVAILABLE_AGGREGATES_SUCCESS,
  payload: aggregates,
});

export const fetchAvailableAggregates = () => dispatch => {
  dispatch(getAvailableAggregates());
  return GranularityAPI.getAvailableAggregates()
    .then(responseJson => {
      dispatch(getAvailableAggregatesSuccess(responseJson || ''));
      return responseJson;
    })
    .catch(error => {
      dispatch(getAvailableAggregatesFailure(error));
    });
};

// ---------------
// Granularities
// ---------------
const getGranularities = () => ({
  type: GET_GRANULARITIES,
});

const getGranularitiesFailure = error => ({
  type: GET_GRANULARITIES_FAILURE,
  payload: error,
});

const getGranularitiesSuccess = granularities => ({
  type: GET_GRANULARITIES_SUCCESS,
  payload: granularities,
});

export const fetchGranularities = hierarchyPath => dispatch => {
  dispatch(getGranularities());
  return GranularityAPI.getGranularities(hierarchyPath)
    .then(responseJson => {
      dispatch(getGranularitiesSuccess(responseJson || ''));
      return responseJson;
    })
    .catch(error => {
      dispatch(getGranularitiesFailure(error));
    });
};

// ---------------
// Add granularity
// ---------------
const startAddGranularity = () => ({
  type: ADD_GRANULARITY,
});

const addGranularityFailure = error => ({
  type: ADD_GRANULARITY_FAILURE,
  payload: error,
});

const addGranularitySuccess = granularities => ({
  type: ADD_GRANULARITY_SUCCESS,
  payload: granularities,
});

export const addGranularity =
  (hierarchyPath, grain) =>
  (dispatch, getState, { dataCache }) => {
    dispatch(startAddGranularity());
    const userEmailId = dataCache.get('currentUser')?.id;

    return GranularityAPI.addGranularity(hierarchyPath, grain, userEmailId)
      .then(responseJson => {
        dispatch(addGranularitySuccess(responseJson || ''));
        return responseJson;
      })
      .catch(error => {
        dispatch(addGranularityFailure(error));
        throw error;
      });
  };

// ---------------
// Remove granularity
// ---------------
const startRemoveGranularity = () => ({
  type: REMOVE_GRANULARITY,
});

const removeGranularityFailure = error => ({
  type: REMOVE_GRANULARITY_FAILURE,
  payload: error,
});

const removeGranularitySuccess = granularities => ({
  type: REMOVE_GRANULARITY_SUCCESS,
  payload: granularities,
});

export const removeGranularity =
  (hierarchyPath, grain) =>
  (dispatch, getState, { dataCache }) => {
    dispatch(startRemoveGranularity());
    const userEmailId = dataCache.get('currentUser')?.id;

    return GranularityAPI.removeGranularity(hierarchyPath, grain, userEmailId)
      .then(responseJson => {
        dispatch(removeGranularitySuccess(responseJson || ''));
        return responseJson;
      })
      .catch(error => {
        dispatch(removeGranularityFailure(error));
        throw error;
      });
  };

// ---------------
// Update granularity
// ---------------
const startUpdateGranularity = () => ({
  type: UPDATE_GRANULARITY,
});

const updateGranularityFailure = error => ({
  type: UPDATE_GRANULARITY_FAILURE,
  payload: error,
});

const updateGranularitySuccess = granularities => ({
  type: UPDATE_GRANULARITY_SUCCESS,
  payload: granularities,
});

export const updateGranularity =
  (hierarchyPath, id, grain) =>
  (dispatch, getState, { dataCache }) => {
    dispatch(startUpdateGranularity());
    const userEmailId = dataCache.get('currentUser')?.id;

    return GranularityAPI.updateGranularity(hierarchyPath, id, grain, userEmailId)
      .then(responseJson => {
        dispatch(updateGranularitySuccess(responseJson || ''));
        return responseJson;
      })
      .catch(error => {
        dispatch(updateGranularityFailure(error));
        throw error;
      });
  };
